import { type AccountInfo, CacheLookupPolicy } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import {
    type Dispatch,
    type PropsWithChildren,
    type ReactNode,
    type SetStateAction,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";

import { ConfigurationManager } from "@/utils";

import { AuthContext } from "./AuthContext";
import { setToken as setTokenInStore } from "./token-store";

const prompt = "select_account";
const tokenBufferTime = 5 * 60; // 5 minutes buffer
const cacheLookupPolicy = CacheLookupPolicy.AccessTokenAndRefreshToken;

/**
 * Parses a JWT token and returns its payload.
 *
 * @param {string} token - The JWT token to parse.
 * @returns {{ exp: number }} The parsed token payload.
 */
const parseToken = (token: string): { exp: number } => {
    const components = token.split(".");
    const rawPayload = components[1];
    const payload = JSON.parse(atob(rawPayload)) as { exp: number };
    return payload;
};

/**
 * Provides authentication context using MSAL.
 *
 * @param {PropsWithChildren} props - The properties for the provider.
 * @returns {ReactNode} AuthProvider component that provides authentication context.
 */
export const AuthProvider = ({ children }: PropsWithChildren): ReactNode => {
    const config = ConfigurationManager.values;
    const { instance: msal } = useMsal();
    const [passageToken, setPassageToken] = useState<string | null>(null);
    const redirectRef = useRef(false);

    const isTokenExpired = useCallback((token: string) => {
        try {
            const payload = parseToken(token);
            const currentTime = Math.floor(Date.now() / 1000);
            const expirationTime = payload.exp;
            return currentTime >= expirationTime - tokenBufferTime;
        } catch {
            return true;
        }
    }, []);

    const fetchToken = useCallback(
        async (
            activeAccount: AccountInfo,
            currentValue: string | null,
            setter: Dispatch<SetStateAction<string | null>>,
            scopes: string[],
        ) => {
            if (currentValue && !isTokenExpired(currentValue)) {
                return;
            }

            const response = await msal.acquireTokenSilent({
                scopes,
                account: activeAccount,
                cacheLookupPolicy,
            });

            setter(response.accessToken);
            setTokenInStore(response.accessToken);
        },
        [isTokenExpired, msal],
    );

    const refreshToken = useCallback(
        async (activeAccount: AccountInfo) => {
            await fetchToken(
                activeAccount,
                passageToken,
                setPassageToken,
                config.passage.scope,
            );
        },
        [config.passage.scope, fetchToken, passageToken],
    );

    const refreshTokenOrLogout = useCallback(async () => {
        const activeAccount = msal.getActiveAccount();

        if (!activeAccount) {
            msal.loginRedirect({ scopes: config.passage.scope, prompt });
            return;
        }

        try {
            await refreshToken(activeAccount);
        } catch {
            msal.loginRedirect({ scopes: config.passage.scope, prompt });
        }
    }, [config.passage.scope, msal, refreshToken]);

    useEffect(() => {
        // Handle the double-render StrictMode issue
        if (redirectRef.current) return;
        redirectRef.current = true;

        const handleRedirect = async () => {
            // Await the redirect promise to ensure the redirect flow is handled
            // If result is non-null, we just completed a redirect login
            const result = await msal.handleRedirectPromise();

            if (result?.account) {
                // Just completed redirect login - set the account and refresh tokens
                msal.setActiveAccount(result.account);
            }

            await refreshTokenOrLogout();
        };

        // Ensure that there is an active account
        handleRedirect();
    }, [refreshTokenOrLogout, msal]);

    return (
        <AuthContext
            value={{
                token: passageToken,
                logout: () => msal.logoutRedirect(),
                refreshToken: refreshTokenOrLogout,
                isTokenExpired,
                isAuthenticated: !!passageToken,
            }}
        >
            {passageToken ? children : null}
        </AuthContext>
    );
};
